/**
 * Payload for "Generar Orden de Produccion"
 * (POST /ms/inventario/private/1.0/agregar/orden/Produccion).
 *
 * The component took the product straight from `.description`:
 *   producto: productoActivo.description
 * so when the item came from the sale JSON (or from the product search)
 * without a `description`, the value was `undefined`. axios drops undefined
 * keys from the JSON body, the request arrived without `producto` and the API
 * answered 500 because `tb_orden_produccion.producto` is NOT NULL without a
 * default (ER_NO_DEFAULT_FOR_FIELD). The same component already resolved the
 * name with a `description || name` fallback for display, but not for the
 * payload that gets persisted.
 *
 * This helper keeps the decision out of the component so it can be verified
 * without a browser: it returns the normalized payload, or null when the OP
 * must not be created at all (the caller shows the notice and does not fire
 * the request).
 *
 * Kept free of imports on purpose.
 */

const texto = (value) => {
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
}

/**
 * @param {any} value tm_ventas.productos (JSON string, array or null)
 * @returns {any[] | null} null when there is nothing usable; never throws
 */
export const parsearProductosVenta = (value) => {
    if (Array.isArray(value)) return value.length ? value : null
    if (typeof value !== 'string') return null
    const text = value.trim()
    if (text === '') return null
    try {
        const parsed = JSON.parse(text)
        return Array.isArray(parsed) && parsed.length ? parsed : null
    } catch (error) {
        return null
    }
}

/**
 * The product autocomplete must be on screen whenever there is no resolved
 * product. If it disappears the user has no way to pick one, so the OP can
 * never be generated (that is how the 500 showed up in production: a
 * `mounted()` that aborted left the form with a hidden selector).
 *
 * @param {{ checkbox?: boolean, productoNombre?: string }} state
 * @returns {boolean}
 */
export const debeMostrarSelectorProducto = ({ checkbox = false, productoNombre = '' } = {}) => Boolean(checkbox) || !productoNombre

/**
 * @param {any} producto product picked in the autocomplete
 * @param {any} dataProducto product inherited from the sale (tm_ventas.productos)
 * @returns {string | null} null when there is nothing to send
 */
export const resolveProductoDescripcion = (producto = null, dataProducto = null) => {
    for (const candidate of [producto, dataProducto]) {
        if (!candidate) continue
        const descripcion = texto(candidate.description) || texto(candidate.name)
        if (descripcion) return descripcion
    }
    return null
}

/**
 * @param {object} source form state plus `producto` / `dataProducto`
 * @returns {object | null} null when the OP must not be created
 */
export const buildOrdenProduccionPayload = (source = {}) => {
    const datos = source || {}
    const producto = resolveProductoDescripcion(datos.producto, datos.dataProducto)
    if (!producto) return null

    return {
        vendedor: datos.vendedor,
        personalCargo: datos.personalCargo,
        fechaEmision: datos.fechaEmision,
        ordenPedido: datos.ordenPedido ? datos.ordenPedido : null,
        producto,
        color: datos.color ? datos.color : 'NO DEFINIDO',
        medida: datos.medida ? datos.medida : 'NO DEFINIDO',
        material: datos.material,
        observaciones: datos.observaciones,
        cantidad: datos.cantidad || 1
    }
}
